import { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { IMapController, IMapData } from '../../../lib/map';

interface AddressData {
  address: string;
  city?: string;
  state?: string;
  county?: string;
  coordinates?: [number, number];
}

interface UseMapControllerOptions {
  isMobile: boolean;
  onAddressSelect?: (addressData: AddressData) => void;
  onError: (error: string) => void;
  enableGeocoding?: boolean;
  enableHistory?: boolean;
  initialCenter?: [number, number];
}

export const useMapController = ({
  isMobile,
  onAddressSelect,
  onError,
  enableGeocoding = true,
  enableHistory = false,
  initialCenter,
}: UseMapControllerOptions) => {
  const [mapContainer, setMapContainer] = useState<HTMLDivElement | null>(null);
  const mapController = useRef<IMapController | null>(null);
  const [loading, setLoading] = useState(false);

  const onAddressSelectRef = useRef(onAddressSelect);
  const onErrorRef = useRef(onError);

  onAddressSelectRef.current = onAddressSelect;
  onErrorRef.current = onError;

  const mapContainerRef = useCallback((element: HTMLDivElement | null) => {
    setMapContainer(element);
  }, []);

  useLayoutEffect(() => {
    if (!mapContainer || mapController.current) {
      return;
    }

    setLoading(true);

    const handleMapDataChange = (data: IMapData) => {
      try {
        if (data.address && data.address.trim() && onAddressSelectRef.current) {
          const addressData: AddressData = {
            address: data.address,
            city: data.city,
            state: data.state,
            county: data.county,
            coordinates: data.center ? [data.center.x, data.center.y] : undefined,
          };

          onAddressSelectRef.current(addressData);
        }
      } catch {
        onErrorRef.current('Error processing address selection');
      }
    };

    const handleMapLoad = (isLoading: boolean) => {
      setLoading(isLoading);
    };

    try {
      import('../../../lib/map')
        .then(({ CompositeMapController }) => {
          try {
            const containerRef = { current: mapContainer };

            const controller = new CompositeMapController({
              ref: containerRef,
              plan: {},
              zoom: 19,
              isMobile,
              center: initialCenter,
              features: {
                markers: true,
                lines: true,
                polygons: true,
                geocoder: enableGeocoding,
                history: enableHistory
                  ? {
                      enabled: true,
                      maxHistorySize: 50,
                      enableKeyboardShortcuts: true,
                    }
                  : false,
              },
            });

            controller.setHandler(handleMapDataChange);
            controller.onLoad = handleMapLoad;

            mapController.current = controller;
            setLoading(false);
          } catch (err) {
            console.error('Error creating CompositeMapController:', err);
            onErrorRef.current('Failed to initialize map. Please try refreshing the page.');
            setLoading(false);
          }
        })
        .catch(err => {
          console.error('Error importing CompositeMapController:', err);
          onErrorRef.current('Failed to load map components. Please try refreshing the page.');
          setLoading(false);
        });
    } catch (err) {
      console.error('Error in map initialization:', err);
      onErrorRef.current('Failed to initialize map. Please try refreshing the page.');
      setLoading(false);
    }

    return () => {
      if (mapController.current) {
        mapController.current = null;
      }
    };
  }, [mapContainer, isMobile, enableGeocoding, enableHistory]);

  return {
    mapContainer: mapContainerRef,
    loading,
    mapController: mapController.current,
  };
};
